import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import CustomSpinner, { CustomSpinnerColor } from '@components/Spinner/CustomSpinner';
import { useAppContext } from '@contexts/AppContext';
import useEventTracking from '@hooks/useEventTracking';
import useSyncDataTableHeaders from '@hooks/useSyncDataTableHeaders';
import {
  Column,
  ColumnDef,
  Row,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ContentType, ImpressionElement, Page, TrackingLocation, impression } from '@tracking';

import { ProjectColumnConfigFn, ProjectData } from '../../interfaces/projects';
import DataTableTbody from './DataTableTbody';
import DataTableThead from './DataTableThead';
import SearchBox from './SearchBox';

interface DataTableProps<TData, TValue> extends ProjectData<TData, TValue> {
  classNameTable?: string;
  classNameTbody?: string;
  classNameTd?: string;
  classNameTh?: string;
  classNameThead?: string;
  columnConfigFn?: ProjectColumnConfigFn;
  pinnedCols: string[];
  pinTableHeader?: boolean;
  projectId: number;
  showSearch?: boolean;
}

export const PIN_LEFT = 'left';

export const isPinnedLeft = <TData, TValue>(column: Column<TData, TValue>) =>
  column.getIsFirstColumn() || column.getIsPinned() === PIN_LEFT;

const DataTable = <TData, TValue>({
  classNameTable,
  classNameTbody,
  classNameTd,
  classNameTh,
  classNameThead,
  columns,
  columnConfigFn,
  data,
  hiddenCols,
  pinnedCols = [],
  pinTableHeader = true,
  projectId,
  showSearch = false,
  sortCols,
}: DataTableProps<TData, TValue>) => {
  const [columnDefs, setColumnDefs] = useState<ColumnDef<TData, TValue>[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [globalFilter, setGlobalFilter] = useState<string>('');
  const stickyTableHeaderRef = useRef(null);
  const fixedTableHeaderRef = useRef(null);
  const { isWebview } = useAppContext();
  useSyncDataTableHeaders({
    pinTableHeader,
    stickyTableHeaderRef: stickyTableHeaderRef,
    fixedTableHeaderRef: fixedTableHeaderRef,
  });

  const { Track, trackEvent } = useEventTracking({
    contentId: projectId,
    contentType: ContentType.Project,
    location: TrackingLocation.Database,
    page: Page.Project,
    pageId: projectId,
  });

  const customSortFn = useCallback(
    (rowA: Row<TData>, rowB: Row<TData>, columnId: string) => {
      let sortCol = columnId;
      if (sortCols.includes(`${columnId}_sort`)) {
        sortCol = `${columnId}_sort`;
      }

      const rawValA = rowA.original[sortCol];
      const rawValB = rowB.original[sortCol];

      if (rawValA == null && rawValB == null) return 0;
      if (rawValA == null) return 1;
      if (rawValB == null) return -1;

      let sortValA: string | number = String(rawValA).toLowerCase();
      let sortValB: string | number = String(rawValB).toLowerCase();

      if (!isNaN(Number(rawValA)) && !isNaN(Number(rawValB))) {
        sortValA = Number(rawValA);
        sortValB = Number(rawValB);
      }

      return sortValA > sortValB ? 1 : -1;
    },
    [sortCols]
  );

  const reactTableColumns = useMemo(() => {
    return columns.map((column) => {
      const configuredColumn = columnConfigFn ? columnConfigFn(column) : column;
      return {
        ...configuredColumn,
        sortingFn: customSortFn,
      };
    });
  }, [columns, customSortFn, columnConfigFn]);

  const columnVisibility = useMemo(
    () =>
      hiddenCols.reduce((acc, col) => {
        acc[col] = false;
        return acc;
      }, {}),
    [hiddenCols]
  );

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGlobalFilter(event.target.value);
  };

  useEffect(() => {
    setColumnDefs(reactTableColumns);
    setIsLoading(false);
  }, [reactTableColumns]);

  const Table = useReactTable({
    columns: columnDefs,
    data,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
    debugHeaders: true,
    debugColumns: true,
    getColumnCanGlobalFilter: () => true,
    enableSortingRemoval: true,
    globalFilterFn: 'includesString',
    initialState: {
      columnVisibility: columnVisibility,
      columnPinning: { left: pinnedCols },
    },
    onGlobalFilterChange: setGlobalFilter,
    state: {
      globalFilter,
    },
  });

  if (isLoading) {
    trackEvent(impression({ impressionElement: ImpressionElement.Project }));
    return (
      <div className="flex justify-center">
        <CustomSpinner color={CustomSpinnerColor.PrimaryMagenta} size={20} />
      </div>
    );
  }

  return (
    <Track>
      {showSearch && <SearchBox handleSearch={handleSearch} value={globalFilter} />}
      <div className="mt-32 sm:mt-48">
        {pinTableHeader && (
          <div
            className={clsx('sticky z-50 translate-x-0 translate-y-0 overflow-x-hidden', {
              'top-0': isWebview,
              'top-56 sm:top-64': !isWebview,
            })}
            ref={stickyTableHeaderRef}
          >
            <table className={clsx(classNameTable, 'w-full border-separate')}>
              <DataTableThead
                classNameTh={classNameTh}
                classNameThead={classNameThead}
                Table={Table}
              />
            </table>
          </div>
        )}
        <div className="max-w-full overflow-x-auto" ref={fixedTableHeaderRef}>
          <table className={clsx(classNameTable, 'border-separate')}>
            <DataTableThead
              classNameTh={classNameTh}
              classNameThead={classNameThead}
              Table={Table}
            />
            <DataTableTbody
              classNameTbody={classNameTbody}
              classNameTd={classNameTd}
              numColumns={columns.length}
              Table={Table}
            />
          </table>
        </div>
      </div>
    </Track>
  );
};

export default DataTable;
